import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import { PostUnreadMentionCount } from './fragments/PostUnreadMentionCount'
import { PostUnreadNoteCount } from './fragments/PostUnreadNoteCount'
import { isOptionalNotesSummaryError } from './isOptionalNotesSummaryError'
import { useNotesReadAction } from './useNotesReadAction'
import type { NotesReadAction } from './useNotesReadAction'
import type { NotesReadState } from './useNotesReadState'

export const MarkNotesReadMutation = graphql(
  /* GraphQL */ `
    mutation MarkNotesRead($input: MarkCollaborationNotesReadInput!) {
      markCollaborationNotesRead(input: $input) {
        __typename
        ... on MarkCollaborationNotesReadSuccess {
          readState {
            readThroughNoteId
            firstUnreadNoteId
            unreadNoteCount
          }
          post {
            ...PostUnreadNoteCount
            ...PostUnreadMentionCount
          }
        }
      }
    }
  `,
  [PostUnreadNoteCount, PostUnreadMentionCount],
)

export function useMarkNotesRead(
  postId: string,
  applyState: (state: NotesReadState) => void,
): NotesReadAction {
  const [mutate] = useMutation(MarkNotesReadMutation, { errorPolicy: 'all' })
  const write = useCallback(
    async (
      selectedPostId: string,
      throughNoteId: string,
    ): Promise<NotesReadState | null> => {
      const response = await mutate({
        variables: { input: { postId: selectedPostId, throughNoteId } },
      })
      const payload = response.data?.markCollaborationNotesRead
      return (!response.error || isOptionalNotesSummaryError(response.error)) &&
        payload?.__typename === 'MarkCollaborationNotesReadSuccess'
        ? payload.readState
        : null
    },
    [mutate],
  )

  return useNotesReadAction(postId, applyState, write)
}
